/**
 * Detect that a NEW build has been deployed while this tab stayed open.
 *
 * Vite fingerprints its bundles, so a deployed fix reaches a user only when the
 * browser fetches a fresh index.html. A tab that was already open keeps running
 * the bundle it loaded, indefinitely. On 13 Sep a Maui LTRG user entered data
 * into a tab running a bundle from before a fix had shipped, 23 minutes after
 * the deploy went live, and saved a value the repaired field would have
 * rejected. Nothing told her, and nothing could.
 *
 * `version.json` is written at build time next to index.html (see the
 * emitBuildVersion plugin in vite.config.ts) and carries the fingerprint of the
 * entry bundle. We read it once at boot, then re-read it on a timer and whenever
 * the user returns to the tab. A changed value means the server is serving a
 * build we are not running.
 *
 * This only ever REPORTS. Reloading without asking would discard whatever the
 * user was typing, which is a worse failure than running a slightly old bundle.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import useRefreshOnFocus from '@/hooks/useRefreshOnFocus';

/** Re-check roughly every 10 minutes; a deploy is not an event worth polling
 *  hard for, and the focus trigger covers the case that actually matters. */
const POLL_INTERVAL_MS = 10 * 60 * 1000;

const VERSION_URL = '/version.json';

async function readDeployedBuild(): Promise<string | null> {
  try {
    // no-store, not no-cache: we must not be served the very artifact whose
    // staleness we are trying to detect.
    const res = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = await res.json();
    return typeof body?.build === 'string' ? body.build : null;
  } catch {
    // Offline, blocked, or the file is not deployed yet. Staying quiet is
    // correct: a failed check is not evidence of a new build.
    return null;
  }
}

export default function useDeploymentVersion(): {
  updateAvailable: boolean;
  reload: () => void;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const bootBuildRef = useRef<string | null>(null);

  const check = useCallback(async () => {
    const deployed = await readDeployedBuild();
    if (!deployed) return;

    if (bootBuildRef.current === null) {
      bootBuildRef.current = deployed;
      return;
    }

    if (deployed !== bootBuildRef.current) setUpdateAvailable(true);
  }, []);

  useEffect(() => {
    void check();
    const id = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [check]);

  // Returning to a tab is the moment a stale bundle is about to be used, so it
  // is the moment worth checking. Shorter throttle than the poll interval.
  useRefreshOnFocus(() => void check(), { minIntervalMs: 60_000 });

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
